import { useState, type FormEvent } from 'react';

import { Button, Input } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  createMyMember,
  EscritoriosError,
  type EscritoriosErrorReason,
  type MemberRecord,
} from '@/lib/escritorios';
import { mapMembersErrorReason } from './error_messages';

const _MIN_PASSWORD = 8;
const _EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CreateMemberDialogProps {
  onSuccess(member: MemberRecord): void;
  onCancel(): void;
}

export function CreateMemberDialog({ onSuccess, onCancel }: CreateMemberDialogProps) {
  const apiClient = useApiClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorReason, setErrorReason] = useState<EscritoriosErrorReason | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setErrorReason(null);
    setClientError(null);

    if (!_EMAIL_RE.test(email.trim())) {
      setClientError(ptBR.members.addDialogEmailInvalid);
      return;
    }
    if (password.length < _MIN_PASSWORD) {
      setClientError(ptBR.members.addDialogPasswordTooShort);
      return;
    }
    if (password !== confirm) {
      setClientError(ptBR.members.addDialogPasswordMismatch);
      return;
    }

    setSubmitting(true);
    try {
      const member = await createMyMember(apiClient, { email: email.trim(), password });
      onSuccess(member);
    } catch (err) {
      const reason: EscritoriosErrorReason =
        err instanceof EscritoriosError ? err.reason : 'unknown';
      setErrorReason(reason);
    } finally {
      setSubmitting(false);
    }
  }

  const errorMsg =
    clientError ?? (errorReason !== null ? mapMembersErrorReason(errorReason) : null);

  return (
    <Dialog open onOpenChange={(open) => !open && !submitting && onCancel()}>
      <DialogContent
        data-testid="member-add-dialog"
        aria-describedby={errorMsg !== null ? 'member-add-error' : undefined}
        hideCloseButton
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{ptBR.members.addDialogTitle}</DialogTitle>
            <DialogDescription>{ptBR.members.addDialogHint}</DialogDescription>
          </DialogHeader>

          <label className="mt-4 flex flex-col gap-1.5">
            <span className="text-body-sm text-fg">{ptBR.members.addDialogEmailLabel}</span>
            <Input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={ptBR.members.addDialogEmailPlaceholder}
              disabled={submitting}
              data-testid="member-add-email-input"
            />
          </label>

          <label className="mt-4 flex flex-col gap-1.5">
            <span className="text-body-sm text-fg">{ptBR.members.addDialogPasswordLabel}</span>
            <Input
              type="password"
              required
              autoComplete="new-password"
              minLength={_MIN_PASSWORD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={ptBR.members.addDialogPasswordPlaceholder}
              disabled={submitting}
              data-testid="member-add-password-input"
            />
          </label>

          <label className="mt-4 flex flex-col gap-1.5">
            <span className="text-body-sm text-fg">
              {ptBR.members.addDialogPasswordConfirmLabel}
            </span>
            <Input
              type="password"
              required
              autoComplete="new-password"
              minLength={_MIN_PASSWORD}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={submitting}
              data-testid="member-add-password-confirm-input"
            />
          </label>

          {errorMsg !== null ? (
            <p
              id="member-add-error"
              role="alert"
              data-testid="member-add-error"
              className="mt-4 border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
            >
              {errorMsg}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={submitting}
              data-testid="member-add-cancel"
            >
              {ptBR.members.addDialogCancel}
            </Button>
            <Button
              type="submit"
              disabled={submitting || email.length === 0 || password.length === 0}
              data-testid="member-add-confirm"
            >
              {submitting ? ptBR.members.saving : ptBR.members.addDialogConfirm}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
